import React, { useState, useRef, useEffect } from "react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSubmit,
  DropdownMenuHeader,
  DropdownMenuTitle,
} from "~/react/ui/dropdown-menu"
import { Button } from "~/react/ui/button"
import { Checkbox } from "~/react/ui/checkbox"
import { Tag, TagLabel } from "~/react/ui/tag"
import { AsyncSelect, Select } from "~/react/ui/select"
import { Input } from "~/react/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/react/ui/tabs"
import { CompanyFiltersType, Location, Company, CompanyType } from "~/types/company"
import { Options } from "react-select"
import { locations_path } from "~/lib/routes"

// Company size options (from Domain::COMPANY_SIZE_MAPPING)
const COMPANY_SIZE_OPTIONS = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1001-5000", "5001-10000", "10001+"]

// Company type options (from Domain::COMPANY_TYPE_LIST)
const COMPANY_TYPE_OPTIONS = [
  "educational",
  "educational institution",
  "government agency",
  "non profit",
  "partnership",
  "privately held",
  "public company",
  "self employed",
  "self owned",
  "sole proprietorship",
]

// Funding series options (from Funding::SERIES_MAPPING)
const FUNDING_SERIES_OPTIONS = [
  { value: "pre_seed", label: "Pre-Seed" },
  { value: "seed", label: "Seed" },
  { value: "pre_series_a", label: "Pre-Series A" },
  { value: "series_a", label: "Series A" },
  { value: "pre_series_b", label: "Pre-Series B" },
  { value: "series_b", label: "Series B" },
  { value: "pre_series_c", label: "Pre-Series C" },
  { value: "series_c+", label: "Series C+" },
  { value: "other", label: "Other" },
]

interface CompanyFiltersProps {
  filters: CompanyFiltersType
  setFilters: (filters: CompanyFiltersType) => void
  clearFilters: () => void
  anyFiltersSet: boolean
  industries?: Array<{ id: number; name: string }> // Optional industries data
  technologies?: Array<{ slug: string; name: string; category: string }> // Optional technologies data
}

export const CompanyFilters: React.FC<CompanyFiltersProps> = ({
  filters,
  setFilters,
  clearFilters,
  anyFiltersSet,
  industries = [],
  technologies = [],
}) => {
  const locationTriggerRef = useRef<HTMLButtonElement>(null)
  const industryTriggerRef = useRef<HTMLButtonElement>(null)
  const sizeTriggerRef = useRef<HTMLButtonElement>(null)
  const typeTriggerRef = useRef<HTMLButtonElement>(null)
  const yearTriggerRef = useRef<HTMLButtonElement>(null)
  const fundingTriggerRef = useRef<HTMLButtonElement>(null)
  const technologiesTriggerRef = useRef<HTMLButtonElement>(null)

  // Local state for each filter
  const [selectedLocation, setSelectedLocation] = useState<Location[]>(filters.location || [])
  const [selectedIndustries, setSelectedIndustries] = useState<number[]>(filters.industries || [])
  const [selectedSizes, setSelectedSizes] = useState<string[]>(filters.companySizes || [])
  const [selectedTypes, setSelectedTypes] = useState<CompanyType[]>(filters.companyTypes || [])
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>(filters.technologies || [])
  const [selectedYearFounded, setSelectedYearFounded] = useState<CompanyFiltersType["yearFounded"]>(filters.yearFounded)
  const [selectedFunding, setSelectedFunding] = useState<CompanyFiltersType["funding"]>(filters.funding)

  // Sync local state with props
  useEffect(() => {
    setSelectedLocation(filters.location || [])
    setSelectedIndustries(filters.industries || [])
    setSelectedSizes(filters.companySizes || [])
    setSelectedTypes(filters.companyTypes || [])
    setSelectedTechnologies(filters.technologies || [])
    setSelectedYearFounded(filters.yearFounded)
    setSelectedFunding(filters.funding)
    // Sync funding date state
    if (filters.funding) {
      setFundingDate(filters.funding.date || "")
      setFundingDateFrom(filters.funding.dateFrom?.toString() || "")
      setFundingDateTo(filters.funding.dateTo?.toString() || "")
    }
  }, [filters])

  const applyFilters = () => {
    setFilters({
      location: selectedLocation,
      industries: selectedIndustries,
      companySizes: selectedSizes,
      companyTypes: selectedTypes,
      technologies: selectedTechnologies,
      yearFounded: selectedYearFounded,
      funding: selectedFunding,
    })
  }

  // Location filter
  const adjustLocationFilter = (selectedOptions: Options<Location>) => {
    const newLocation = (selectedOptions || []).map((option: any) => ({
      value: option.value,
      type: option.type,
      label: option.label,
      included: true,
    }))
    setSelectedLocation(newLocation)
  }

  const loadLocations = (inputValue: string, callback: (options: Options<Location>) => void) => {
    fetch(locations_path({ query: inputValue }))
      .then((response) => response.json())
      .then((data) => [...new Set(data)])
      .then((data) => {
        return data
          .filter((x, index, col) => col.findIndex((e) => e.value == x.value) === index)
          .reduce((acc: GroupedOption[], item) => {
            if (!acc.find((group) => group.label === item.type)) {
              acc.push({ label: item.type, options: [] })
            }
            acc.find((group) => group.label === item.type)!.options.push(item)
            return acc
          }, [])
      })
      .then((data: GroupedOption[]) => callback(data))
      .catch((error) => console.error(error))
  }

  const changeLocationInclusion = (location: Location, included: boolean) => {
    const newLocation = selectedLocation.map((loc) => {
      if (loc.label === location.label) {
        return { ...loc, included }
      }
      return loc
    })
    setSelectedLocation(newLocation)
  }

  type GroupedOption = {
    label: string
    options: Options<Location>
  }

  const formatGroupLabel = (data: GroupedOption) => <div className="optgroup-heading">{data.label}</div>

  const locationTags = selectedLocation?.map((location) => {
    const { label, value, included } = location
    return (
      <div className="tags-list flex-nowrap mt-2" key={label}>
        <Tag variant={included ? "" : "danger"}>
          <TagLabel>
            {label}
            <span
              className="far fa-times ml-1"
              onClick={() => setSelectedLocation((prev) => prev.filter((loc) => loc.value !== value))}
            ></span>
          </TagLabel>
        </Tag>
        <div className="tags-list__actions">
          <button
            className={`tags-list__action ${included ? "active" : ""}`}
            onClick={() => changeLocationInclusion(location, true)}
          >
            Include
          </button>
          <button
            className={`tags-list__action ${included ? "" : "active"}`}
            onClick={() => changeLocationInclusion(location, false)}
          >
            Exclude
          </button>
        </div>
      </div>
    )
  })

  const locationFilterBody = (
    <>
      <AsyncSelect<Location, false, GroupedOption>
        placeholder="Search for locations..."
        isMulti={true}
        value={selectedLocation?.map((location) => ({
          value: location.value,
          label: location.label,
        }))}
        loadOptions={loadLocations}
        formatGroupLabel={formatGroupLabel}
        onChange={adjustLocationFilter}
      />
      {locationTags}
      <DropdownMenuSubmit onClick={() => applyFilters()}>Apply filters</DropdownMenuSubmit>
    </>
  )

  // Industry filter
  const industryOptions = industries.map((ind) => ({
    value: ind.id,
    label: ind.name,
  }))

  const industryFilterBody = (
    <>
      <Select
        options={industryOptions}
        placeholder="Select industries..."
        isMulti={true}
        value={selectedIndustries.map((id) => industryOptions.find((opt) => opt.value === id))}
        onChange={(selected) => {
          setSelectedIndustries((selected || []).map((opt: any) => opt.value))
        }}
      />
      <DropdownMenuSubmit onClick={() => applyFilters()}>Apply filters</DropdownMenuSubmit>
    </>
  )

  // Company size filter
  const sizeFilterBody = (
    <>
      {COMPANY_SIZE_OPTIONS.map((size) => (
        <DropdownMenuItem key={size}>
          <Checkbox
            checked={selectedSizes.includes(size)}
            id={`filter-size-${size}`}
            onChange={() =>
              setSelectedSizes((prev) => (prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]))
            }
          />
          <label htmlFor={`filter-size-${size}`}>{size}</label>
        </DropdownMenuItem>
      ))}
      <DropdownMenuSubmit onClick={() => applyFilters()}>Apply filters</DropdownMenuSubmit>
    </>
  )

  // Company type filter
  const typeOptions = COMPANY_TYPE_OPTIONS.map((type) => ({
    value: type,
    label: type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
  }))

  const adjustCompanyTypeFilter = (selectedOptions: Options<{ value: string; label: string }>) => {
    const newTypes = (selectedOptions || []).map((option: any) => {
      // Check if this type already exists in selectedTypes
      const existing = selectedTypes.find((t) => t.value === option.value)
      return {
        value: option.value,
        label: option.label,
        included: existing ? existing.included : true, // Default to included
      }
    })
    setSelectedTypes(newTypes)
  }

  const changeCompanyTypeInclusion = (companyType: CompanyType, included: boolean) => {
    const newTypes = selectedTypes.map((type) => {
      if (type.value === companyType.value) {
        return { ...type, included }
      }
      return type
    })
    setSelectedTypes(newTypes)
  }

  const companyTypeTags = selectedTypes?.map((companyType) => {
    const { label, value, included } = companyType
    return (
      <div className="tags-list flex-nowrap mt-2" key={value}>
        <Tag variant={included ? undefined : "danger"}>
          <TagLabel>
            {label}
            <span
              className="far fa-times ml-1"
              onClick={() => setSelectedTypes((prev) => prev.filter((type) => type.value !== value))}
            ></span>
          </TagLabel>
        </Tag>
        <div className="tags-list__actions">
          <button
            className={`tags-list__action ${included ? "active" : ""}`}
            onClick={() => changeCompanyTypeInclusion(companyType, true)}
          >
            Include
          </button>
          <button
            className={`tags-list__action ${included ? "" : "active"}`}
            onClick={() => changeCompanyTypeInclusion(companyType, false)}
          >
            Exclude
          </button>
        </div>
      </div>
    )
  })

  const typeFilterBody = (
    <>
      <Select
        options={typeOptions}
        placeholder="Search for a company type..."
        isMulti={true}
        value={selectedTypes.map((type) => ({
          value: type.value,
          label: type.label,
        }))}
        onChange={(selected: any) => {
          adjustCompanyTypeFilter(selected || [])
        }}
        components={{
          MultiValue: () => null,
          MultiValueRemove: () => null,
          ClearIndicator: () => null,
        }}
        styles={{
          valueContainer: (base: any) => ({
            ...base,
            padding: "0 var(--spacing-02)",
          }),
        }}
      />
      {companyTypeTags}
      <DropdownMenuSubmit onClick={() => applyFilters()}>Apply filters</DropdownMenuSubmit>
    </>
  )

  // Year founded filter
  const [yearFoundedMode, setYearFoundedMode] = useState<"interval" | "specific">(
    selectedYearFounded?.mode || "interval",
  )
  const [yearFrom, setYearFrom] = useState<string>(selectedYearFounded?.from?.toString() || "")
  const [yearTo, setYearTo] = useState<string>(selectedYearFounded?.to?.toString() || "")
  const [specificYears, setSpecificYears] = useState<number[]>(selectedYearFounded?.years || [])
  const [yearInput, setYearInput] = useState<string>("")

  const addSpecificYear = () => {
    const year = parseInt(yearInput, 10)
    if (!isNaN(year) && year >= 1000 && year <= new Date().getFullYear()) {
      if (!specificYears.includes(year)) {
        setSpecificYears([...specificYears, year].sort())
      }
      setYearInput("")
    }
  }

  const yearFoundedFilterBody = (
    <>
      <Tabs value={yearFoundedMode} onValueChange={(v) => setYearFoundedMode(v as "interval" | "specific")}>
        <TabsList>
          <TabsTrigger value="interval">Interval</TabsTrigger>
          <TabsTrigger value="specific">Specific</TabsTrigger>
        </TabsList>
        <TabsContent value="interval">
          <div className="d-flex gap-2">
            <div>
              <label className="h-label" htmlFor="year-from">
                From
              </label>
              <input
                id="year-from"
                className="h-input h-input--block"
                type="number"
                min="1000"
                max={new Date().getFullYear()}
                value={yearFrom}
                onChange={(e) => setYearFrom(e.target.value)}
                placeholder="Year"
              />
            </div>
            <div>
              <label className="h-label" htmlFor="year-to">
                To
              </label>
              <input
                id="year-to"
                className="h-input h-input--block"
                type="number"
                min="1000"
                max={new Date().getFullYear()}
                value={yearTo}
                onChange={(e) => setYearTo(e.target.value)}
                placeholder="Year"
              />
            </div>
          </div>
        </TabsContent>
        <TabsContent value="specific">
          <div>
            <input
              className="h-input h-input--block"
              type="number"
              min="1000"
              max={new Date().getFullYear()}
              value={yearInput}
              onChange={(e) => setYearInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  addSpecificYear()
                }
              }}
              onBlur={addSpecificYear}
              placeholder="Add year"
            />
            {specificYears.length > 0 && (
              <div className="tags-list flex-wrap mt-2">
                {specificYears.map((year) => (
                  <Tag key={year}>
                    <TagLabel>
                      {year}
                      <span
                        className="far fa-times ml-1"
                        onClick={() => setSpecificYears(specificYears.filter((y) => y !== year))}
                      ></span>
                    </TagLabel>
                  </Tag>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
      <DropdownMenuSubmit
        onClick={() => {
          const yearFounded =
            yearFoundedMode === "interval"
              ? {
                  mode: "interval" as const,
                  from: yearFrom ? parseInt(yearFrom, 10) : undefined,
                  to: yearTo ? parseInt(yearTo, 10) : undefined,
                }
              : {
                  mode: "specific" as const,
                  years: specificYears,
                }
          setSelectedYearFounded(
            yearFounded.from || yearFounded.to || yearFounded.years?.length ? yearFounded : undefined,
          )
          applyFilters()
        }}
      >
        Apply filters
      </DropdownMenuSubmit>
    </>
  )

  // Funding filter
  const [fundingSeries, setFundingSeries] = useState<string[]>(selectedFunding?.series || [])
  const [fundingAmountFrom, setFundingAmountFrom] = useState<string>(selectedFunding?.amountFrom?.toString() || "")
  const [fundingAmountTo, setFundingAmountTo] = useState<string>(selectedFunding?.amountTo?.toString() || "")
  const [fundingDate, setFundingDate] = useState<string>(selectedFunding?.date || "")
  const [fundingDateFrom, setFundingDateFrom] = useState<string>(selectedFunding?.dateFrom?.toString() || "")
  const [fundingDateTo, setFundingDateTo] = useState<string>(selectedFunding?.dateTo?.toString() || "")

  const FUNDING_DATE_OPTIONS = [
    { value: "", label: "" },
    { value: "7d", label: "Past 7 days" },
    { value: "1m", label: "Past month" },
    { value: "3m", label: "Past 3 months" },
    { value: "6m", label: "Past 6 months" },
    { value: "1y", label: "Past year" },
    { value: "custom", label: "Custom..." },
  ]

  const fundingFilterBody = (
    <>
      <DropdownMenuHeader>
        <DropdownMenuTitle>Series</DropdownMenuTitle>
      </DropdownMenuHeader>
      {FUNDING_SERIES_OPTIONS.map((series) => (
        <DropdownMenuItem key={series.value}>
          <Checkbox
            checked={fundingSeries.includes(series.value)}
            id={`filter-funding-${series.value}`}
            onChange={() =>
              setFundingSeries((prev) =>
                prev.includes(series.value) ? prev.filter((s) => s !== series.value) : [...prev, series.value],
              )
            }
          />
          <label htmlFor={`filter-funding-${series.value}`}>{series.label}</label>
        </DropdownMenuItem>
      ))}
      <DropdownMenuHeader>
        <DropdownMenuTitle>Series amount</DropdownMenuTitle>
      </DropdownMenuHeader>
      <div className="d-flex gap-2">
        <div className="form-group">
          <label className="h-label h-label--regular" htmlFor="funding-amount-from">
            From
          </label>
          <div className="h-input-group">
            <span className="h-input-group__text">
              <span className="far fa-dollar"></span>
            </span>
            <Input
              type="text"
              id="funding-amount-from"
              value={fundingAmountFrom}
              onChange={(e) => setFundingAmountFrom(e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="h-label h-label--regular" htmlFor="funding-amount-to">
            To
          </label>
          <div className="h-input-group">
            <span className="h-input-group__text">
              <span className="far fa-dollar"></span>
            </span>
            <Input
              type="text"
              id="funding-amount-to"
              value={fundingAmountTo}
              onChange={(e) => setFundingAmountTo(e.target.value)}
            />
          </div>
        </div>
      </div>
      <DropdownMenuHeader>
        <DropdownMenuTitle>Date</DropdownMenuTitle>
      </DropdownMenuHeader>
      <div className="form-group">
        <Select
          options={FUNDING_DATE_OPTIONS}
          placeholder="Select date range..."
          value={FUNDING_DATE_OPTIONS.find((opt) => opt.value === fundingDate)}
          onChange={(selected: any) => {
            setFundingDate(selected?.value || "")
          }}
        />
      </div>
      {fundingDate === "custom" && (
        <div className="signal-custom-dates mt-2">
          <div className="form-group">
            <label className="h-label h-label--regular" htmlFor="funding-date-from">
              From
            </label>
            <div className="h-input-group">
              <Input
                type="number"
                id="funding-date-from"
                min={0}
                max={365}
                value={fundingDateFrom}
                onChange={(e) => setFundingDateFrom(e.target.value)}
              />
              <span className="h-input-group__text">days ago</span>
            </div>
          </div>
          <div className="form-group">
            <label className="h-label h-label--regular" htmlFor="funding-date-to">
              To
            </label>
            <div className="h-input-group">
              <Input
                type="number"
                id="funding-date-to"
                min={0}
                max={365}
                value={fundingDateTo}
                onChange={(e) => setFundingDateTo(e.target.value)}
              />
              <span className="h-input-group__text">days ago</span>
            </div>
          </div>
        </div>
      )}
      <DropdownMenuSubmit
        onClick={() => {
          const funding =
            fundingSeries.length > 0 ||
            fundingAmountFrom ||
            fundingAmountTo ||
            fundingDate ||
            (fundingDate === "custom" && (fundingDateFrom || fundingDateTo))
              ? {
                  series: fundingSeries,
                  amountFrom: fundingAmountFrom ? parseInt(fundingAmountFrom.replace(/,/g, ""), 10) : undefined,
                  amountTo: fundingAmountTo ? parseInt(fundingAmountTo.replace(/,/g, ""), 10) : undefined,
                  date: fundingDate || undefined,
                  dateFrom: fundingDate === "custom" && fundingDateFrom ? parseInt(fundingDateFrom, 10) : undefined,
                  dateTo: fundingDate === "custom" && fundingDateTo ? parseInt(fundingDateTo, 10) : undefined,
                }
              : undefined
          setSelectedFunding(funding)
          applyFilters()
        }}
      >
        Apply filters
      </DropdownMenuSubmit>
    </>
  )

  // Technologies filter
  const technologiesByCategory = technologies.reduce(
    (acc, tech) => {
      if (!acc[tech.category]) {
        acc[tech.category] = []
      }
      acc[tech.category].push({
        value: tech.slug,
        label: tech.name,
        category: tech.category,
      })
      return acc
    },
    {} as Record<string, Array<{ value: string; label: string; category: string }>>,
  )

  const technologyOptions = Object.entries(technologiesByCategory).flatMap(([category, techs]) =>
    techs.map((tech) => ({
      ...tech,
      group: category,
    })),
  )

  const technologiesFilterBody = (
    <>
      <Select
        options={technologyOptions}
        placeholder="Select technologies..."
        isMulti={true}
        value={selectedTechnologies.map((slug) => technologyOptions.find((opt) => opt.value === slug))}
        onChange={(selected) => {
          setSelectedTechnologies((selected || []).map((opt: any) => opt.value))
        }}
      />
      <DropdownMenuSubmit onClick={() => applyFilters()}>Apply filters</DropdownMenuSubmit>
    </>
  )

  return (
    <div className="ds-filters flex-nowrap">
      <div className="d-flex flex-wrap gap-2">
        {/* Location Filter */}
        <div className="h-filter">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="h-filter__button" size="sm" ref={locationTriggerRef}>
                Location
                <span className="far fa-angle-down" aria-hidden="true"></span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="overflow-visible location-filter-dropdown">
              {locationFilterBody}
            </DropdownMenuContent>
          </DropdownMenu>
          {filters.location?.length > 0 && (
            <div className="h-filter__value">
              {filters.location.length} location
              {filters.location.length !== 1 ? "s" : ""}
              <button
                className="btn-empty h-filter__remove"
                onClick={() => {
                  setSelectedLocation([])
                  setFilters({
                    ...filters,
                    location: [],
                  })
                }}
              >
                <span className="far fa-times" aria-hidden="true"></span>
              </button>
            </div>
          )}
        </div>

        {/* Industry Filter */}
        {industries.length > 0 && (
          <div className="h-filter">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="h-filter__button" size="sm" ref={industryTriggerRef}>
                  Industry
                  <span className="far fa-angle-down" aria-hidden="true"></span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>{industryFilterBody}</DropdownMenuContent>
            </DropdownMenu>
            {filters.industries?.length > 0 && (
              <div className="h-filter__value">
                {filters.industries.length} industr
                {filters.industries.length !== 1 ? "ies" : "y"}
                <button
                  className="btn-empty h-filter__remove"
                  onClick={() => {
                    setSelectedIndustries([])
                    setFilters({
                      ...filters,
                      industries: [],
                    })
                  }}
                >
                  <span className="far fa-times" aria-hidden="true"></span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Company Size Filter */}
        <div className="h-filter">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="h-filter__button" size="sm" ref={sizeTriggerRef}>
                Size
                <span className="far fa-angle-down" aria-hidden="true"></span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>{sizeFilterBody}</DropdownMenuContent>
          </DropdownMenu>
          {filters.companySizes?.length > 0 && (
            <div className="h-filter__value">
              {filters.companySizes.length} size
              {filters.companySizes.length !== 1 ? "s" : ""}
              <button
                className="btn-empty h-filter__remove"
                onClick={() => {
                  setSelectedSizes([])
                  setFilters({
                    ...filters,
                    companySizes: [],
                  })
                }}
              >
                <span className="far fa-times" aria-hidden="true"></span>
              </button>
            </div>
          )}
        </div>

        {/* Company Type Filter */}
        <div className="h-filter">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="h-filter__button" size="sm" ref={typeTriggerRef}>
                Type
                <span className="far fa-angle-down" aria-hidden="true"></span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>{typeFilterBody}</DropdownMenuContent>
          </DropdownMenu>
          {filters.companyTypes?.length > 0 && (
            <div className="h-filter__value">
              {filters.companyTypes.length} type
              {filters.companyTypes.length !== 1 ? "s" : ""}
              <button
                className="btn-empty h-filter__remove"
                onClick={() => {
                  setSelectedTypes([])
                  setFilters({
                    ...filters,
                    companyTypes: [],
                  })
                }}
              >
                <span className="far fa-times" aria-hidden="true"></span>
              </button>
            </div>
          )}
        </div>

        {/* Year Founded Filter */}
        <div className="h-filter">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="h-filter__button" size="sm" ref={yearTriggerRef}>
                Year Founded
                <span className="far fa-angle-down" aria-hidden="true"></span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="overflow-visible">{yearFoundedFilterBody}</DropdownMenuContent>
          </DropdownMenu>
          {filters.yearFounded && (
            <div className="h-filter__value">
              {filters.yearFounded.mode === "interval"
                ? `${filters.yearFounded.from || "?"} - ${filters.yearFounded.to || "?"}`
                : `${filters.yearFounded.years?.length || 0} years`}
              <button
                className="btn-empty h-filter__remove"
                onClick={() => {
                  setSelectedYearFounded(undefined)
                  setFilters({
                    ...filters,
                    yearFounded: undefined,
                  })
                }}
              >
                <span className="far fa-times" aria-hidden="true"></span>
              </button>
            </div>
          )}
        </div>

        {/* Funding Filter */}
        <div className="h-filter">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="h-filter__button" size="sm" ref={fundingTriggerRef}>
                Funding
                <span className="far fa-angle-down" aria-hidden="true"></span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="overflow-visible">{fundingFilterBody}</DropdownMenuContent>
          </DropdownMenu>
          {filters.funding && (
            <div className="h-filter__value">
              Funding
              <button
                className="btn-empty h-filter__remove"
                onClick={() => {
                  setSelectedFunding(undefined)
                  setFilters({
                    ...filters,
                    funding: undefined,
                  })
                }}
              >
                <span className="far fa-times" aria-hidden="true"></span>
              </button>
            </div>
          )}
        </div>

        {/* Technologies Filter */}
        {technologies.length > 0 && (
          <div className="h-filter">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="h-filter__button" size="sm" ref={technologiesTriggerRef}>
                  Technologies
                  <span className="far fa-angle-down" aria-hidden="true"></span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="overflow-visible">{technologiesFilterBody}</DropdownMenuContent>
            </DropdownMenu>
            {filters.technologies?.length > 0 && (
              <div className="h-filter__value">
                {filters.technologies.length} technolog
                {filters.technologies.length !== 1 ? "ies" : "y"}
                <button
                  className="btn-empty h-filter__remove"
                  onClick={() => {
                    setSelectedTechnologies([])
                    setFilters({
                      ...filters,
                      technologies: [],
                    })
                  }}
                >
                  <span className="far fa-times" aria-hidden="true"></span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Clear All Button */}
        {anyFiltersSet && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </div>
    </div>
  )
}

// Filter function to apply filters to companies
export const filterCompanies = (companies: Company[], filters: CompanyFiltersType): Company[] => {
  let filtered = companies

  // Filter by location
  if (filters.location.length > 0) {
    const includeLocations = filters.location.filter((loc) => loc.included)
    const excludeLocations = filters.location.filter((loc) => !loc.included)

    filtered = filtered.filter((company) => {
      // Check exclude locations first
      if (excludeLocations.some((loc) => matchesLocation(company, loc))) {
        return false
      }

      // If include locations exist, company must match at least one
      if (includeLocations.length > 0) {
        return includeLocations.some((loc) => matchesLocation(company, loc))
      }

      return true
    })
  }

  // Filter by industries
  if (filters.industries.length > 0) {
    // Note: This requires industry ID mapping from the backend
    // For now, we'll skip this filter as we need the industry_category field
    // which should match the industry IDs
    // filtered = filtered.filter((company) => {
    //   // Match industry by category field (would need industry ID mapping)
    //   return filters.industries.includes(company.industry_id)
    // })
  }

  // Filter by company sizes
  if (filters.companySizes.length > 0) {
    filtered = filtered.filter((company) => filters.companySizes.includes(company.size || ""))
  }

  // Filter by company types
  if (filters.companyTypes.length > 0) {
    const includeTypes = filters.companyTypes.filter((type) => type.included)
    const excludeTypes = filters.companyTypes.filter((type) => !type.included)

    filtered = filtered.filter((company) => {
      // Check exclude types first
      if (excludeTypes.some((type) => company.company_type === type.value)) {
        return false
      }

      // If include types exist, company must match at least one
      if (includeTypes.length > 0) {
        return includeTypes.some((type) => company.company_type === type.value)
      }

      return true
    })
  }

  // Filter by technologies
  if (filters.technologies.length > 0) {
    filtered = filtered.filter((company) =>
      filters.technologies.some((tech) => company.technologies_slugs.includes(tech)),
    )
  }

  // Filter by year founded
  if (filters.yearFounded) {
    if (filters.yearFounded.mode === "interval") {
      if (filters.yearFounded.from) {
        filtered = filtered.filter(
          (company) => company.founded_year && company.founded_year >= filters.yearFounded!.from!,
        )
      }
      if (filters.yearFounded.to) {
        filtered = filtered.filter(
          (company) => company.founded_year && company.founded_year <= filters.yearFounded!.to!,
        )
      }
    } else if (filters.yearFounded.years) {
      filtered = filtered.filter((company) => filters.yearFounded!.years!.includes(company.founded_year || 0))
    }
  }

  // Filter by funding
  if (filters.funding) {
    if (filters.funding.series.length > 0) {
      // Filter by funding series
      const seriesTypes = filters.funding.series.flatMap((s) => {
        const mapping: Record<string, string | string[]> = {
          pre_seed: "Pre-Seed",
          seed: "Seed",
          pre_series_a: "Pre-Series A",
          series_a: "Series A",
          pre_series_b: "Pre-Series B",
          series_b: "Series B",
          pre_series_c: "Pre-Series C",
          "series_c+": [
            "Series C",
            "Pre-Series D",
            "Series D",
            "Pre-Series E",
            "Series E",
            "Pre-Series F",
            "Series F",
            "Pre-Series G",
            "Series G",
            "Pre-Series H",
            "Series H",
            "Pre-Series I",
            "Series I",
          ],
          other: "Other",
        }
        return Array.isArray(mapping[s]) ? mapping[s] : [mapping[s]]
      })
      filtered = filtered.filter((company) => {
        if (!company.last_funding_type) return false
        return seriesTypes.includes(company.last_funding_type)
      })
    }

    if (filters.funding.amountFrom || filters.funding.amountTo) {
      filtered = filtered.filter((company) => {
        if (!company.total_raised) return false
        if (filters.funding!.amountFrom && company.total_raised < filters.funding!.amountFrom) {
          return false
        }
        if (filters.funding!.amountTo && company.total_raised > filters.funding!.amountTo) {
          return false
        }
        return true
      })
    }

    // Filter by funding date
    if (filters.funding.date || filters.funding.dateFrom || filters.funding.dateTo) {
      filtered = filtered.filter((company) => {
        if (!company.last_funding_date) return false
        const fundingDate = new Date(company.last_funding_date)
        const today = new Date()

        // Handle predefined date ranges
        if (filters.funding!.date && filters.funding!.date !== "custom") {
          let daysAgo = 0
          switch (filters.funding!.date) {
            case "7d":
              daysAgo = 7
              break
            case "1m":
              daysAgo = 30
              break
            case "3m":
              daysAgo = 90
              break
            case "6m":
              daysAgo = 180
              break
            case "1y":
              daysAgo = 365
              break
          }
          if (daysAgo > 0) {
            const cutoffDate = new Date(today)
            cutoffDate.setDate(cutoffDate.getDate() - daysAgo)
            if (fundingDate < cutoffDate) return false
          }
        }

        // Handle custom date range (days ago)
        if (filters.funding!.date === "custom") {
          if (filters.funding!.dateFrom) {
            const fromDate = new Date(today)
            fromDate.setDate(fromDate.getDate() - filters.funding!.dateFrom!)
            if (fundingDate < fromDate) return false
          }
          if (filters.funding!.dateTo) {
            const toDate = new Date(today)
            toDate.setDate(toDate.getDate() - filters.funding!.dateTo!)
            if (fundingDate > toDate) return false
          }
        }

        return true
      })
    }
  }

  return filtered
}

const matchesLocation = (company: Company, location: Location): boolean => {
  if (location.type === "country") {
    return company.country === location.value
  }
  if (location.type === "state") {
    return company.state === location.value
  }
  if (location.type === "city") {
    return company.city === location.value
  }
  // For continent and business_region, we'd need location mappings
  // This would require additional data from the backend
  return false
}
